import { useState, useEffect } from 'react';
import { Toast } from '../types/utils';

// Maintain a global counter for toast IDs to ensure uniqueness across all components
let globalToastIdCounter = 0;

// Define input type for showToast function
type ToastInput = string | { message: string; type: 'success' | 'danger' | 'warning' | 'info' };

/**
 * Custom hook for managing toast notifications
 * @param duration Duration in milliseconds for which toasts are visible
 * @returns Object with toasts array and showToast function
 */
export function useToast(duration = 4500) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  /**
   * Show a toast notification
   * @param input The message string or a configuration object with message and type
   * @param type Optional type parameter for backward compatibility
   */
  const showToast = (
    input: ToastInput | string, 
    type?: 'success' | 'danger' | 'warning' | 'info'
  ) => {
    // Use the global counter to ensure unique IDs across the application
    const id = globalToastIdCounter++;
    
    // Handle various input formats
    let message: string;
    let toastType: 'success' | 'danger' | 'warning' | 'info';
    
    if (typeof input === 'string') {
      message = input;
      toastType = type || 'success';
    } else {
      message = input.message;
      toastType = input.type;
    }
    
    setToasts(prev => [...prev, { id, message, type: toastType, isVisible: true }]);
    
    // Hide the toast after the specified duration
    setTimeout(() => {
      setToasts(prev => 
        prev.map(toast => 
          toast.id === id ? { ...toast, isVisible: false } : toast
        )
      );
    }, duration);

    // Remove the toast from the array after it's hidden
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, duration + 500);
  };

  // Clean up all toasts on component unmount
  useEffect(() => {
    return () => {
      // This cleanup function runs when the component unmounts
      setToasts([]);
    };
  }, []);

  return { toasts, showToast };
} 